import { useState } from 'react';
import { motion } from 'motion/react';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { signIn, errorText } from '../lib/pb';

export default function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignIn = async () => {
    setBusy(true);
    setError(null);
    try {
      await signIn();
      onSignedIn();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md mx-auto my-12 p-8 text-center zen-card space-y-6"
    >
      <div className="relative mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-natural-bg">
        <span className="text-4xl">🙇</span>
        <div className="absolute inset-0 rounded-full border-2 border-dashed border-natural-sage/40 animate-spin-slower" />
      </div>

      <div className="space-y-2">
        <h2 className="font-serif text-2xl font-medium tracking-tight">
          AI Endorser Content Factory
        </h2>
        <p className="text-xs text-natural-text/70 leading-relaxed max-w-sm mx-auto">
          Sign in with your Impact Brands Google account to start creating AI Endorser content
        </p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded-xl text-xs flex items-start gap-2 text-left">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <button
        onClick={handleSignIn}
        disabled={busy}
        className="w-full flex items-center justify-center gap-3 px-5 py-3 bg-white border border-natural-border
                   rounded-xl hover:bg-natural-bg hover:border-natural-sage/50 active:scale-[0.98]
                   transition-all duration-300 shadow-xs cursor-pointer group
                   disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
      >
        <svg
          className="w-5 h-5 shrink-0 transition-transform duration-300 group-hover:scale-110"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
          />
        </svg>
        <span className="font-semibold text-sm group-hover:text-natural-sage transition-colors">
          {busy ? 'Connecting…' : 'Sign in with Google'}
        </span>
      </button>

      <div className="pt-4 border-t border-natural-border flex justify-center items-center gap-1.5 text-[10px] text-natural-text/60 font-mono">
        <CheckCircle size={10} className="text-natural-sage" />
        <span>@THEIMPACTBRANDS.COM ACCOUNTS ONLY</span>
      </div>
    </motion.div>
  );
}
